import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ButtonV2 } from "#src/commonV2/Button";
import { FormSelectV2 } from "#src/commonV2/form/Select";
import { TagsInput } from "#src/commonV2/TagsInput";
import { memberRoleItems } from "#src/config/filter/workspace";
import patterns from "#src/config/patterns";
import { MemberRoleEnum } from "#src/config/filter/workspace/enum";
import workspaceServices, {
  type IWorkspaceItem,
} from "#src/services/workspace";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";

const roleItems = memberRoleItems.filter(
  (item) => item.key !== MemberRoleEnum.OWNER
);

type IAddMembersModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  currentWorkspace: IWorkspaceItem | undefined;
  updateMemberList: () => void;
};

export const AddMembersModal = ({
  open,
  onClose,
  workspaceId,
  currentWorkspace,
  updateMemberList,
}: IAddMembersModalProps) => {
  const { t } = useTranslation("workspace", {
    keyPrefix: "member.addMemberModal",
  });

  // --------------- DATA ---------------

  const [isLoading, setIsLoading] = useState(false);
  const [isValidDomain, setIsValidDomain] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [formData, setFormData] = useState<{
    emails: string[];
    role: string | undefined;
  }>({
    role: roleItems[0].value,
    emails: [],
  });

  useEffect(() => {
    if (open) {
      setNewEmail("");
      setIsValidDomain(true);
      setFormData({
        role: roleItems[0].value,
        emails: [],
      });
    }
  }, [open]);

  // --------------- COMPUTED ---------------
  const selectedRole = useMemo(() => {
    return roleItems.find((i) => i.value === formData.role) || roleItems[0];
  }, [formData.role]);

  const payload = useMemo(() => {
    if (formData.emails.length === 0) {
      return [{ email: newEmail, role: formData.role || "" }];
    }
    return formData.emails.map((e) => ({
      email: e,
      role: formData.role || "",
    }));
  }, [newEmail, formData]);

  const btnAddMemberDisabled = useMemo(() => {
    if (formData.emails.length === 0) {
      if (newEmail) {
        return !patterns.EMAIL.test(newEmail);
      }
      return true;
    }
    return false;
  }, [newEmail, formData.emails]);

  const validEmailByDomains = (emails = formData.emails) => {
    let result = true;
    if (
      currentWorkspace &&
      currentWorkspace.enterprise_email_enabled &&
      !btnAddMemberDisabled
    ) {
      const domains = currentWorkspace.enterprise_email_domains;
      if (emails.length === 0 && !!newEmail) {
        result = domains.includes(newEmail.split("@")[1]);
      } else if (emails.length > 0) {
        result = !emails.find((e) => !domains.includes(e.split("@")[1]));
      }
    }
    setIsValidDomain(result);
    return result;
  };

  // --------------- METHODS ---------------

  const addMembers = async () => {
    if (workspaceId && formData.role && validEmailByDomains()) {
      setIsLoading(true);
      workspaceServices
        .add_member(workspaceId, payload)
        .then((response) => {
          if (response.success) {
            updateMemberList();
            onClose();
          }
        })
        .catch((error) => {
          apiErrorHandler(error, {
            toastMessage: t("notification.addMember.fail"),
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const changeEmails = (emails: string[]) => {
    const validEmails = emails.filter((e) => patterns.EMAIL.test(e));
    setFormData({ ...formData, emails: validEmails });
    validEmailByDomains(validEmails);
  };

  return (
    <ModalV2 open={open} onChangeOpen={onClose} className="w-[38rem]">
      <span className="text-lg font-semibold text-gray-v2-900">
        {t("title")}
      </span>
      {/* Form */}
      <div className="flex flex-col gap-6">
        {/* Emails */}
        <div className="flex flex-col gap-1.5">
          <p className="text-gray-v2-700 text-sm font-medium">
            {t("field.email.label")}{" "}
            <span className="text-sm text-brand-v2-600">*</span>
          </p>
          <TagsInput
            tags={formData.emails}
            setTags={changeEmails}
            badgeColor="gray"
            disabled={isLoading}
            placeholder={t("field.email.placeholder")}
            searchText={newEmail}
            setSearchText={(v) => {
              setNewEmail(v);
              setIsValidDomain(true);
            }}
          />
          <p className="text-gray-v2-600 text-sm">
            <Trans
              t={t}
              i18nKey={"field.email.hint"}
              components={{
                span: <span className="font-semibold text-gray-v2-900" />,
              }}
            />
          </p>
          {!isValidDomain && (
            <p className="text-md font-semibold text-error-v2-600">
              {t("field.email.invalidDomainMessage")}
            </p>
          )}
        </div>
        {/* Emails end */}

        {/* Role */}
        <FormSelectV2
          label={t("field.role.label")}
          options={roleItems}
          selected={selectedRole}
          disabled={isLoading}
          onChangeSelected={(v) => setFormData({ ...formData, role: v.value })}
        />
        {/* Role end */}
        {/* Footer */}
        <div className="flex gap-2 justify-end">
          <ButtonV2
            variant="secondary"
            size="lg"
            color="gray"
            onClick={onClose}
          >
            <p>{t("button.cancel")}</p>
          </ButtonV2>

          <ButtonV2
            variant="primary"
            size="lg"
            disabled={btnAddMemberDisabled}
            pending={isLoading}
            onClick={addMembers}
          >
            <p>{t("button.addMember")}</p>
          </ButtonV2>
        </div>
        {/* Footer end */}
      </div>
      {/* Form end */}
    </ModalV2>
  );
};
